import React, { useState, useEffect } from 'react';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  Cell, ScatterChart, Scatter
} from 'recharts';

// Define types for our component
interface JsonToChartProps {}

// Chart type options
type ChartType = 'line' | 'bar' | 'pie' | 'area' | 'scatter';

// Chart theme options
type ChartTheme = 'default' | 'vivid' | 'pastel' | 'monochrome' | 'dark';

// Mapping fields
interface FieldMapping {
  xAxis: string;
  yAxis: string;
  category?: string;
  value?: string;
}

// Chart configuration
interface ChartConfig {
  title: string;
  showLegend: boolean;
  showGrid: boolean;
  showTooltip: boolean;
  aspectRatio: number; // height / width
}

const JsonToChart: React.FC<JsonToChartProps> = () => {
  // State for the JSON input
  const [jsonInput, setJsonInput] = useState<string>('');
  // State for parsed JSON data
  const [parsedData, setParsedData] = useState<any[] | null>(null);
  // State for available fields
  const [availableFields, setAvailableFields] = useState<string[]>([]);
  // State for error messages
  const [error, setError] = useState<string>('');
  // State for chart type
  const [chartType, setChartType] = useState<ChartType>('bar');
  // State for chart theme
  const [chartTheme, setChartTheme] = useState<ChartTheme>('default');
  // State for field mapping
  const [fieldMapping, setFieldMapping] = useState<FieldMapping>({
    xAxis: '',
    yAxis: '',
    category: '',
    value: ''
  });
  // State for chart config
  const [chartConfig, setChartConfig] = useState<ChartConfig>({
    title: 'Chart Title',
    showLegend: true,
    showGrid: true,
    showTooltip: true,
    aspectRatio: 0.6
  });
  // State for loading indicator
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // State for showing advanced options
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  // State to track if chart is generated
  const [isChartGenerated, setIsChartGenerated] = useState<boolean>(false);
  // State for the file name
  const [fileName, setFileName] = useState<string>('chart_data');

  // Example data for different chart types
  const exampleData = {
    line: [
      { month: 'Jan', sales: 4000, profit: 2400 },
      { month: 'Feb', sales: 3000, profit: 1398 },
      { month: 'Mar', sales: 2000, profit: 9800 },
      { month: 'Apr', sales: 2780, profit: 3908 },
      { month: 'May', sales: 1890, profit: 4800 },
      { month: 'Jun', sales: 2390, profit: 3800 },
      { month: 'Jul', sales: 3490, profit: 4300 }
    ],
    bar: [
      { category: 'Category A', value: 4000 },
      { category: 'Category B', value: 3000 },
      { category: 'Category C', value: 2000 },
      { category: 'Category D', value: 2780 },
      { category: 'Category E', value: 1890 },
      { category: 'Category F', value: 2390 }
    ],
    pie: [
      { name: 'Group A', value: 400 },
      { name: 'Group B', value: 300 },
      { name: 'Group C', value: 300 },
      { name: 'Group D', value: 200 }
    ],
    area: [
      { month: 'Jan', value: 4000 },
      { month: 'Feb', value: 3000 },
      { month: 'Mar', value: 2000 },
      { month: 'Apr', value: 2780 },
      { month: 'May', value: 1890 },
      { month: 'Jun', value: 2390 },
      { month: 'Jul', value: 3490 }
    ],
    scatter: [
      { x: 100, y: 200, z: 200 },
      { x: 120, y: 100, z: 260 },
      { x: 170, y: 300, z: 400 },
      { x: 140, y: 250, z: 280 },
      { x: 150, y: 400, z: 500 },
      { x: 110, y: 280, z: 200 }
    ]
  };

  // Get color scheme based on theme
  const getColorScheme = () => {
    switch (chartTheme) {
      case 'vivid':
        return ['#FF5733', '#33FF57', '#3357FF', '#F033FF', '#FF33A1', '#33FFF0'];
      case 'pastel':
        return ['#FFB6C1', '#98FB98', '#ADD8E6', '#F0E68C', '#DDA0DD', '#FFDAB9'];
      case 'monochrome':
        return ['#2F4F4F', '#708090', '#778899', '#A9A9A9', '#C0C0C0', '#D3D3D3'];
      case 'dark':
        return ['#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#a4de6c', '#d0ed57'];
      default: // 'default'
        return ['#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#a4de6c', '#d0ed57'];
    }
  };

  // Handle JSON input change
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setJsonInput(e.target.value);
    setError('');
    setIsChartGenerated(false);
  };

  // Handle file name change
  const handleFileNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFileName(e.target.value);
  };

  // Use example data
  const useExampleData = () => {
    const data = exampleData[chartType];
    setJsonInput(JSON.stringify(data, null, 2));
    setError('');
    setIsChartGenerated(false);
    
    // Set default field mappings based on example data structure
    if (data && data.length > 0) {
      const firstItem = data[0];
      const keys = Object.keys(firstItem);
      
      if (chartType === 'line' || chartType === 'area') {
        setFieldMapping({
          xAxis: 'month',
          yAxis: 'sales',
          category: '',
          value: ''
        });
      } else if (chartType === 'bar') {
        setFieldMapping({
          xAxis: 'category',
          yAxis: 'value',
          category: '',
          value: ''
        });
      } else if (chartType === 'pie') {
        setFieldMapping({
          xAxis: '',
          yAxis: '',
          category: 'name',
          value: 'value'
        });
      } else if (chartType === 'scatter') {
        setFieldMapping({
          xAxis: 'x',
          yAxis: 'y',
          category: '',
          value: ''
        });
      }
    }
  };

  // Beautify/Format JSON
  const beautifyJson = () => {
    try {
      if (!jsonInput.trim()) {
        setError('Please enter JSON data to format');
        return;
      }
      
      const parsedJson = JSON.parse(jsonInput);
      const beautified = JSON.stringify(parsedJson, null, 2);
      setJsonInput(beautified);
      setError('');
    } catch (e) {
      setError(`Invalid JSON format: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  // Validate JSON and ensure it's an array
  const validateJsonArray = (jsonString: string): any[] | null => {
    try {
      const parsed = JSON.parse(jsonString);
      
      if (!Array.isArray(parsed)) {
        setError('JSON must be an array of objects for chart creation');
        return null;
      }
      
      if (parsed.length === 0) {
        setError('JSON array must contain at least one item');
        return null;
      }
      
      // Check if all items are objects
      if (!parsed.every(item => typeof item === 'object' && item !== null && !Array.isArray(item))) {
        setError('All items in the array must be objects');
        return null;
      }
      
      return parsed;
    } catch (e) {
      setError(`Invalid JSON format: ${e instanceof Error ? e.message : String(e)}`);
      return null;
    }
  };

  // Extract available fields from JSON data
  const extractFields = (data: any[]): string[] => {
    if (!data || data.length === 0) return [];
    
    // Get all possible keys from all objects
    const allKeys = new Set<string>();
    data.forEach(item => {
      Object.keys(item).forEach(key => allKeys.add(key));
    });
    
    return Array.from(allKeys);
  };

  // Update field mapping
  const updateFieldMapping = (field: keyof FieldMapping, value: string) => {
    setFieldMapping(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Update chart config
  const updateChartConfig = (field: keyof ChartConfig, value: any) => {
    setChartConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Clear all inputs and reset states
  const clearAll = () => {
    setJsonInput('');
    setParsedData(null);
    setAvailableFields([]);
    setError('');
    setIsChartGenerated(false);
    setFieldMapping({
      xAxis: '',
      yAxis: '',
      category: '',
      value: ''
    });
    setChartConfig({
      title: 'Chart Title',
      showLegend: true,
      showGrid: true,
      showTooltip: true,
      aspectRatio: 0.6
    });
    setFileName('chart_data');
  };

  // Prepare data for chart
  const generateChart = () => {
    setError('');
    setIsLoading(true);
    setIsChartGenerated(false);
    
    if (!jsonInput.trim()) {
      setError('Please enter JSON data');
      setIsLoading(false);
      return;
    }

    const validData = validateJsonArray(jsonInput);
    if (!validData) {
      setIsLoading(false);
      return;
    }

    // Extract available fields
    const fields = extractFields(validData);
    setAvailableFields(fields);

    // Set default field mappings if not already set
    if (!fieldMapping.xAxis && fields.length > 0) {
      updateFieldMapping('xAxis', fields[0]);
    }
    
    if (!fieldMapping.yAxis && fields.length > 1) {
      updateFieldMapping('yAxis', fields[1]);
    }
    
    if (chartType === 'pie' && !fieldMapping.category && fields.length > 0) {
      updateFieldMapping('category', fields[0]);
    }
    
    if (chartType === 'pie' && !fieldMapping.value && fields.length > 1) {
      updateFieldMapping('value', fields[1]);
    }

    setParsedData(validData);
    setIsChartGenerated(true);
    setIsLoading(false);
  };

  // Render the chart based on type
  const renderChart = () => {
    if (!parsedData || !isChartGenerated) return null;
    
    const colors = getColorScheme();
    const containerHeight = 400 * chartConfig.aspectRatio;
    
    switch (chartType) {
      case 'line':
        return (
          <ResponsiveContainer width="100%" height={containerHeight}>
            <LineChart
              data={parsedData}
              margin={{ top: 20, right: 30, left: 20, bottom: 10 }}
            >
              {chartConfig.showGrid && <CartesianGrid strokeDasharray="3 3" />}
              <XAxis dataKey={fieldMapping.xAxis} />
              <YAxis />
              {chartConfig.showTooltip && <Tooltip />}
              {chartConfig.showLegend && <Legend />}
              {availableFields
                .filter(field => field !== fieldMapping.xAxis && typeof parsedData[0][field] === 'number')
                .map((field, index) => (
                  <Line
                    key={field}
                    type="monotone"
                    dataKey={field}
                    stroke={colors[index % colors.length]}
                    activeDot={{ r: 8 }}
                  />
                ))}
            </LineChart>
          </ResponsiveContainer>
        );
        
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height={containerHeight}>
            <BarChart
              data={parsedData}
              margin={{ top: 20, right: 30, left: 20, bottom: 10 }}
            >
              {chartConfig.showGrid && <CartesianGrid strokeDasharray="3 3" />}
              <XAxis dataKey={fieldMapping.xAxis} />
              <YAxis />
              {chartConfig.showTooltip && <Tooltip />}
              {chartConfig.showLegend && <Legend />}
              {availableFields
                .filter(field => field !== fieldMapping.xAxis && typeof parsedData[0][field] === 'number')
                .map((field, index) => (
                  <Bar
                    key={field}
                    dataKey={field}
                    fill={colors[index % colors.length]}
                  />
                ))}
            </BarChart>
          </ResponsiveContainer>
        );
        
      case 'pie':
        return (
          <ResponsiveContainer width="100%" height={containerHeight}>
            <PieChart>
              <Pie
                data={parsedData}
                dataKey={fieldMapping.value || 'value'}
                nameKey={fieldMapping.category}
                cx="50%"
                cy="50%"
                outerRadius={80}
                fill="#8884d8"
                label
              >
                {parsedData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                ))}
              </Pie>
              {chartConfig.showTooltip && <Tooltip />}
              {chartConfig.showLegend && <Legend />}
            </PieChart>
          </ResponsiveContainer>
        );
        
      case 'area':
        return (
          <ResponsiveContainer width="100%" height={containerHeight}>
            <AreaChart
              data={parsedData}
              margin={{ top: 20, right: 30, left: 20, bottom: 10 }}
            >
              {chartConfig.showGrid && <CartesianGrid strokeDasharray="3 3" />}
              <XAxis dataKey={fieldMapping.xAxis} />
              <YAxis />
              {chartConfig.showTooltip && <Tooltip />}
              {chartConfig.showLegend && <Legend />}
              {availableFields
                .filter(field => field !== fieldMapping.xAxis && typeof parsedData[0][field] === 'number')
                .map((field, index) => (
                  <Area
                    key={field}
                    type="monotone"
                    dataKey={field}
                    stroke={colors[index % colors.length]}
                    fill={colors[index % colors.length]}
                    fillOpacity={0.3}
                  />
                ))}
            </AreaChart>
          </ResponsiveContainer>
        );
        
      case 'scatter':
        return (
          <ResponsiveContainer width="100%" height={containerHeight}>
            <ScatterChart
              margin={{ top: 20, right: 30, left: 20, bottom: 10 }}
            >
              {chartConfig.showGrid && <CartesianGrid strokeDasharray="3 3" />}
              <XAxis type="number" dataKey={fieldMapping.xAxis} name={fieldMapping.xAxis} />
              <YAxis type="number" dataKey={fieldMapping.yAxis} name={fieldMapping.yAxis} />
              {chartConfig.showTooltip && <Tooltip cursor={{ strokeDasharray: '3 3' }} />}
              {chartConfig.showLegend && <Legend />}
              <Scatter
                name={`${fieldMapping.xAxis} vs ${fieldMapping.yAxis}`}
                data={parsedData}
                fill={colors[0]}
              />
            </ScatterChart>
          </ResponsiveContainer>
        );
        
      default:
        return null;
    }
  };

  // Generate react component code
  const generateChartCode = (): string => {
    if (!parsedData || !isChartGenerated) return '';
    
    let imports = `import React from 'react';\nimport { `;
    let chartSpecific = '';
    let chartCode = '';
    
    switch (chartType) {
      case 'line':
        imports += `LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';\n\n`;
        chartSpecific = availableFields
          .filter(field => field !== fieldMapping.xAxis && typeof parsedData[0][field] === 'number')
          .map((field, index) => {
            return `            <Line 
              type="monotone" 
              dataKey="${field}" 
              stroke={\`\${colors[${index % getColorScheme().length}]}\`}
              activeDot={{ r: 8 }} 
            />`;
          })
          .join('\n');
        break;
      
      case 'bar':
        imports += `BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';\n\n`;
        chartSpecific = availableFields
          .filter(field => field !== fieldMapping.xAxis && typeof parsedData[0][field] === 'number')
          .map((field, index) => {
            return `            <Bar 
              dataKey="${field}" 
              fill={\`\${colors[${index % getColorScheme().length}]}\`} 
            />`;
          })
          .join('\n');
        break;
      
      case 'pie':
        imports += `PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';\n\n`;
        chartSpecific = `            <Pie
              data={data}
              dataKey="${fieldMapping.value}"
              nameKey="${fieldMapping.category}"
              cx="50%"
              cy="50%"
              outerRadius={80}
              fill="#8884d8"
              label
            >
              {data.map((entry, index) => (
                <Cell key={\`cell-\${index}\`} fill={\`\${colors[index % colors.length]}\`} />
              ))}
            </Pie>`;
        break;
      
      case 'area':
        imports += `AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';\n\n`;
        chartSpecific = availableFields
          .filter(field => field !== fieldMapping.xAxis && typeof parsedData[0][field] === 'number')
          .map((field, index) => {
            return `            <Area 
              type="monotone" 
              dataKey="${field}" 
              stroke={\`\${colors[${index % getColorScheme().length}]}\`}
              fill={\`\${colors[${index % getColorScheme().length}]}\`}
              fillOpacity={0.3} 
            />`;
          })
          .join('\n');
        break;
      
      case 'scatter':
        imports += `ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';\n\n`;
        chartSpecific = `            <Scatter 
              name={\`\${props.xAxisName || '${fieldMapping.xAxis}'} vs \${props.yAxisName || '${fieldMapping.yAxis}'}\`} 
              data={data} 
              fill={\`\${colors[0]}\`} 
            />`;
        break;
      
      default:
        return '';
    }
    
    chartCode = `const ${fileName.replace(/[^a-zA-Z0-9]/g, '') || 'MyChart'} = (props) => {
  // Chart data
  const data = ${JSON.stringify(parsedData, null, 2)};
  
  // Color scheme
  const colors = ${JSON.stringify(getColorScheme())};
  
  // Chart container style
  const containerStyle = {
    width: '100%',
    height: ${400 * chartConfig.aspectRatio},
    margin: '0 auto',
  };
  
  // Chart title style
  const titleStyle = {
    textAlign: 'center',
    marginBottom: '20px',
    fontFamily: 'Arial, sans-serif',
    fontSize: '18px',
    fontWeight: 'bold',
  };
  
  return (
    <div>
      {props.showTitle !== false && (
        <h2 style={titleStyle}>{props.title || "${chartConfig.title}"}</h2>
      )}
      <div style={containerStyle}>
        <ResponsiveContainer width="100%" height="100%">
          <${chartType.charAt(0).toUpperCase() + chartType.slice(1)}Chart
            data={props.data || data}
            margin={{ top: 20, right: 30, left: 20, bottom: 10 }}
          >
            ${chartConfig.showGrid ? '<CartesianGrid strokeDasharray="3 3" />' : ''}
            ${chartType !== 'pie' ? `<XAxis dataKey="${fieldMapping.xAxis}" />` : ''}
            ${chartType !== 'pie' ? '<YAxis />' : ''}
            ${chartConfig.showTooltip ? '<Tooltip />' : ''}
            ${chartConfig.showLegend ? '<Legend />' : ''}
${chartSpecific}
          </${chartType.charAt(0).toUpperCase() + chartType.slice(1)}Chart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default ${fileName.replace(/[^a-zA-Z0-9]/g, '') || 'MyChart'};`;

    return imports + chartCode;
  };

  // Download chart component
  const downloadChartComponent = () => {
    const code = generateChartCode();
    const blob = new Blob([code], { type: 'text/javascript' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    link.href = url;
    link.download = `${fileName || 'MyChart'}.tsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Handle chart type change
  useEffect(() => {
    // Reset field mapping when chart type changes
    if (parsedData && parsedData.length > 0) {
      const fields = extractFields(parsedData);
      
      if (chartType === 'pie') {
        // For pie chart, we need category and value
        setFieldMapping({
          xAxis: '',
          yAxis: '',
          category: fields[0] || '',
          value: fields.find(field => typeof parsedData[0][field] === 'number') || fields[1] || ''
        });
      } else {
        // For other charts, we need xAxis and yAxis
        setFieldMapping({
          xAxis: fields[0] || '',
          yAxis: fields.find(field => typeof parsedData[0][field] === 'number') || fields[1] || '',
          category: '',
          value: ''
        });
      }
    }
  }, [chartType, parsedData]);

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">JSON to Chart Converter</h1>
      
      {/* Description */}
      <div className="mb-6 bg-blue-50 p-4 rounded-md border border-blue-100">
        <p className="text-blue-800">
          Transform your JSON data into beautiful, interactive charts. Paste your JSON data below to generate various types of charts including line, bar, pie, area, and scatter plots.
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Input Section */}
        <div className="flex flex-col h-full">
          <div className="bg-white p-4 rounded-md shadow-sm mb-4 flex-grow">
            <div className="flex justify-between items-center mb-3">
              <label htmlFor="jsonInput" className="font-medium text-gray-700">
                JSON Input
              </label>
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={useExampleData}
                >
                  Use Example
                </button>
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={beautifyJson}
                >
                  Format JSON
                </button>
                <button
                  className="text-sm text-gray-600 hover:text-gray-800"
                  onClick={() => setJsonInput('')}
                >
                  Clear
                </button>
              </div>
            </div>
            <textarea
              id="jsonInput"
              className="w-full h-80 p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
              value={jsonInput}
              onChange={handleInputChange}
              placeholder='Paste your JSON data here, e.g., [{"month": "Jan", "sales": 4000}, {"month": "Feb", "sales": 3000}]'
            />
          </div>
          
          {/* Configuration */}
          <div className="bg-white p-4 rounded-md shadow-sm">
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label htmlFor="chartType" className="block text-sm font-medium text-gray-700 mb-1">
                  Chart Type
                </label>
                <select
                  id="chartType"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  value={chartType}
                  onChange={(e) => setChartType(e.target.value as ChartType)}
                >
                  <option value="bar">Bar Chart</option>
                  <option value="line">Line Chart</option>
                  <option value="pie">Pie Chart</option>
                  <option value="area">Area Chart</option>
                  <option value="scatter">Scatter Plot</option>
                </select>
              </div>
              
              <div>
                <label htmlFor="chartTheme" className="block text-sm font-medium text-gray-700 mb-1">
                  Chart Theme
                </label>
                <select
                  id="chartTheme"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  value={chartTheme}
                  onChange={(e) => setChartTheme(e.target.value as ChartTheme)}
                >
                  <option value="default">Default</option>
                  <option value="vivid">Vivid</option>
                  <option value="pastel">Pastel</option>
                  <option value="monochrome">Monochrome</option>
                  <option value="dark">Dark</option>
                </select>
              </div>
            </div>
            
            <div className="mb-4">
              <label htmlFor="fileName" className="block text-sm font-medium text-gray-700 mb-1">
                Component Name
              </label>
              <input
                type="text"
                id="fileName"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                value={fileName}
                onChange={handleFileNameChange}
                placeholder="Enter component name"
              />
            </div>
            
            <div className="mb-4">
              <button
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                <span>{showAdvanced ? 'Hide' : 'Show'} Advanced Options</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-4 w-4 ml-1 transition-transform ${showAdvanced ? 'transform rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
            
            {showAdvanced && (
              <div className="space-y-4 p-3 bg-gray-50 rounded-md mb-4">
                {/* Field mapping for different chart types */}
                {isChartGenerated && (
                  <div>
                    <h3 className="font-medium text-gray-700 mb-2">Field Mapping</h3>
                    <div className="grid grid-cols-2 gap-3">
                      {chartType !== 'pie' && (
                        <>
                          <div>
                            <label htmlFor="xAxisField" className="block text-sm text-gray-700 mb-1">
                              X-Axis Field
                            </label>
                            <select
                              id="xAxisField"
                              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                              value={fieldMapping.xAxis}
                              onChange={(e) => updateFieldMapping('xAxis', e.target.value)}
                            >
                              {availableFields.map(field => (
                                <option key={field} value={field}>{field}</option>
                              ))}
                            </select>
                          </div>
                          
                          <div>
                            <label htmlFor="yAxisField" className="block text-sm text-gray-700 mb-1">
                              Y-Axis Field
                            </label>
                            <select
                              id="yAxisField"
                              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                              value={fieldMapping.yAxis}
                              onChange={(e) => updateFieldMapping('yAxis', e.target.value)}
                            >
                              {availableFields.map(field => (
                                <option key={field} value={field}>{field}</option>
                              ))}
                            </select>
                          </div>
                        </>
                      )}
                      
                      {chartType === 'pie' && (
                        <>
                          <div>
                            <label htmlFor="categoryField" className="block text-sm text-gray-700 mb-1">
                              Category Field
                            </label>
                            <select
                              id="categoryField"
                              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                              value={fieldMapping.category}
                              onChange={(e) => updateFieldMapping('category', e.target.value)}
                            >
                              {availableFields.map(field => (
                                <option key={field} value={field}>{field}</option>
                              ))}
                            </select>
                          </div>
                          
                          <div>
                            <label htmlFor="valueField" className="block text-sm text-gray-700 mb-1">
                              Value Field
                            </label>
                            <select
                              id="valueField"
                              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                              value={fieldMapping.value}
                              onChange={(e) => updateFieldMapping('value', e.target.value)}
                            >
                              {availableFields.map(field => (
                                <option key={field} value={field}>{field}</option>
                              ))}
                            </select>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Chart configuration */}
                <div>
                  <h3 className="font-medium text-gray-700 mb-2">Chart Configuration</h3>
                  <div className="space-y-3">
                    <div>
                      <label htmlFor="chartTitle" className="block text-sm text-gray-700 mb-1">
                        Chart Title
                      </label>
                      <input
                        type="text"
                        id="chartTitle"
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        value={chartConfig.title}
                        onChange={(e) => updateChartConfig('title', e.target.value)}
                        placeholder="Enter chart title"
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="showLegend"
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          checked={chartConfig.showLegend}
                          onChange={(e) => updateChartConfig('showLegend', e.target.checked)}
                        />
                        <label htmlFor="showLegend" className="ml-2 block text-sm text-gray-700">
                          Show Legend
                        </label>
                      </div>
                      
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="showGrid"
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          checked={chartConfig.showGrid}
                          onChange={(e) => updateChartConfig('showGrid', e.target.checked)}
                        />
                        <label htmlFor="showGrid" className="ml-2 block text-sm text-gray-700">
                          Show Grid
                        </label>
                      </div>
                      
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="showTooltip"
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          checked={chartConfig.showTooltip}
                          onChange={(e) => updateChartConfig('showTooltip', e.target.checked)}
                        />
                        <label htmlFor="showTooltip" className="ml-2 block text-sm text-gray-700">
                          Show Tooltip
                        </label>
                      </div>
                    </div>
                    
                    <div>
                      <label htmlFor="aspectRatio" className="block text-sm text-gray-700 mb-1">
                        Chart Height Ratio: {chartConfig.aspectRatio.toFixed(1)}
                      </label>
                      <input
                        type="range"
                        id="aspectRatio"
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                        min="0.3"
                        max="1.0"
                        step="0.1"
                        value={chartConfig.aspectRatio}
                        onChange={(e) => updateChartConfig('aspectRatio', parseFloat(e.target.value))}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <div className="mt-4 flex justify-between">
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                onClick={generateChart}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </div>
                ) : (
                  'Generate Chart'
                )}
              </button>
              
              <button
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                onClick={clearAll}
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
        
        {/* Output Section */}
        <div className="bg-white p-4 rounded-md shadow-sm h-full flex flex-col">
          <div className="flex justify-between items-center mb-3">
            <label className="font-medium text-gray-700">
              Chart Preview
            </label>
            
            {isChartGenerated && (
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={downloadChartComponent}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download Component
                </button>
              </div>
            )}
          </div>
          
          <div className="flex-grow flex flex-col">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md mb-3">
                <p className="font-medium">Error</p>
                <p>{error}</p>
              </div>
            )}
            
            {!error && !isChartGenerated && (
              <div className="bg-gray-50 border border-gray-200 text-gray-500 p-4 rounded-md flex-grow flex items-center justify-center">
                <div className="text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  <p>
                    Chart preview will appear here after generating
                  </p>
                </div>
              </div>
            )}
            
            {!error && isChartGenerated && (
              <div className="flex-grow flex flex-col">
                <div className="bg-gray-50 border border-gray-200 rounded-md p-4 mb-4 flex-grow">
                  {renderChart()}
                </div>
                
                <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
                  <h3 className="font-medium text-gray-800 mb-2">React Component Code:</h3>
                  <div className="overflow-auto h-64 p-3 bg-gray-800 text-gray-200 rounded-md font-mono text-xs whitespace-pre">
                    {generateChartCode()}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Information Section */}
      <div className="mt-8 bg-white p-6 rounded-md shadow-sm">
        <h2 className="text-lg font-semibold mb-4">About JSON to Chart Conversion</h2>
        
        <div className="space-y-4">
          <div>
            <h3 className="font-medium text-gray-800 mb-2">How This Converter Works</h3>
            <p className="text-gray-600">
              This tool transforms your JSON data into interactive charts and visualizations. Simply paste your JSON array,
              select a chart type, customize your options, and generate a beautiful chart with a downloadable React component.
            </p>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Features</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li><strong>Multiple Chart Types</strong> - Line, bar, pie, area, and scatter visualizations</li>
              <li><strong>Interactive Preview</strong> - See your chart before downloading the component</li>
              <li><strong>Custom Themes</strong> - Choose from various color schemes to fit your design</li>
              <li><strong>Field Mapping</strong> - Map JSON fields to chart dimensions</li>
              <li><strong>Customizable Configuration</strong> - Control legends, grids, tooltips, and more</li>
              <li><strong>Component Generation</strong> - Create a reusable React component for your project</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Tips for JSON Data</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>Data must be an array of objects with consistent properties</li>
              <li>For line and bar charts, include at least one categorical field for the x-axis and numerical fields for the y-axis</li>
              <li>For pie charts, include one field for categories and one for values</li>
              <li>For scatter plots, include two numerical fields for x and y coordinates</li>
              <li>Use the "Format JSON" button to properly format and validate your JSON data before generating a chart</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Using the Generated Component</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>The downloaded TypeScript React component uses the Recharts library</li>
              <li>Include it in your React project and import the required dependencies</li>
              <li>The component accepts props for customization including <code>data</code>, <code>title</code>, and <code>showTitle</code></li>
              <li>You can further customize the component by modifying the downloaded code</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JsonToChart;